import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { celebrate } from '@/design-system/effects'
import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { Countdown } from '@/features/event/components/Countdown'
import { usePublicEvent } from '@/features/event/api'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useCastVote, useVotingCategories, useWithdrawVote } from '../api'
import { formatClock, useCountdown } from '../countdown'
import {
  forgetBallot,
  personalLink,
  rememberPersonalLink,
  storedBallot,
  useGuestBallot,
  useVoteLink,
} from '../ballot'
import type { VoteScore, VotingCategory } from '../types'

/**
 * VOTE NOW 🎭 — la voix de la salle : 70 % de la note de chaque tour,
 * les 30 % restants venant du jury du concours. Aucun score ici avant la
 * publication des scores du concours.
 *
 * Conçue pour un pouce, à bout de bras, dans une salle sombre et bruyante :
 * de grandes cartes, un état visible en un coup d'œil, et un motif clair quand
 * le vote n'est pas possible.
 *
 * Deux façons d'arriver ici, et la page ne fait pas de différence ensuite :
 *
 * * avec son **lien personnel**, reçu à la commande — `?token=…&attendee_id=…` ;
 * * en scannant le **QR posé sur la table**, sans rien dans l'URL. La page
 *   ouvre alors un bulletin de passage et le garde dans le navigateur, ce qui
 *   empêche de voter deux fois en rechargeant.
 *
 * Le bulletin de passage n'est délivré que si l'organisateur a ouvert le vote
 * au-delà des personnes contrôlées à l'entrée ; sinon la page le dit.
 */
export function VotePage() {
  const { t, i18n } = useTranslation()
  // L'erreur appartient à UNE catégorie : affichée partout, un « déjà voté »
  // en catégorie Miss apparaissait aussi sous Master et sous chaque tour.
  const [failure, setFailure] = useState<{ categoryId: string; message: string } | null>(null)

  function explain(cause: unknown): string {
    if (cause instanceof ApiError) {
      for (const key of [`vote.reason.${cause.code}`, `vote.error.${cause.code}`]) {
        if (i18n.exists(key)) return t(key)
      }
      return cause.message
    }
    return t('vote.error.network')
  }
  const [searchParams] = useSearchParams()
  const linkToken = searchParams.get('token')
  const linkAttendeeId = searchParams.get('attendee_id')
  // Arrivé par le lien d'un candidat : on ouvre la page sur sa fiche.
  const highlight = searchParams.get('candidat')

  const eventQuery = usePublicEvent()
  // Le bulletin n'est demandé que faute de lien personnel : l'ouvrir pour
  // quelqu'un qui a déjà son droit de vote créerait un votant fantôme.
  const ballot = useGuestBallot(eventQuery.data?.slug, !linkToken || !linkAttendeeId)

  const token = linkToken ?? (ballot.status === 'ready' ? ballot.ballot.token : null)
  const attendeeId =
    linkAttendeeId ?? (ballot.status === 'ready' ? ballot.ballot.attendee_id : null)

  const categoriesQuery = useVotingCategories(token, attendeeId)

  // Le billet et le QR de la table, vus par le même navigateur : on les
  // relie, pour qu'une même personne ne vote pas une fois par chaque porte.
  // Le lien personnel est retenu ici ; le bulletin de passage l'est déjà.
  const slug = eventQuery.data?.slug
  const personal =
    linkToken && linkAttendeeId ? { token: linkToken, attendee_id: linkAttendeeId } : null
  useEffect(() => {
    if (personal) rememberPersonalLink(slug, personal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, linkToken, linkAttendeeId])
  useVoteLink(
    personal ?? personalLink(slug),
    personal ? storedBallot(slug) : ballot.status === 'ready' ? ballot.ballot : null,
    () => void categoriesQuery.refetch(),
  )

  // La fiche visée est amenée sous les yeux une fois la liste chargée.
  // Sans cela, le lien ouvre la page en haut et le destinataire cherche —
  // c'est exactement ce qu'un lien direct est censé lui éviter.
  useEffect(() => {
    if (!highlight || categoriesQuery.isPending) return
    const target = document.getElementById(`candidat-${highlight}`)
    target?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'center',
    })
  }, [highlight, categoriesQuery.isPending])
  const cast = useCastVote(token, attendeeId)
  const withdraw = useWithdrawVote(token, attendeeId)

  if (!token || !attendeeId) {
    if (ballot.status === 'loading' || eventQuery.isPending) {
      return (
        <Container className="py-24">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-8 h-64 w-full" />
        </Container>
      )
    }

    // Trois situations très différentes, et une seule phrase les couvrait
    // toutes : « lien de vote incomplet ». Elle accusait le lien alors que,
    // neuf fois sur dix, le vote n'était simplement pas encore ouvert — et
    // elle renvoyait chercher un QR personnel qui n'existe pas.
    const closed = ballot.status === 'closed'
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-md text-content-primary">
          {closed ? t('vote.notOpen') : t('vote.noLink')}
        </h1>
        <p className="mt-4 max-w-lg text-content-secondary">
          {closed
            ? t('vote.notOpenHelp')
            : ballot.status === 'error'
              ? ballot.message
              : t('vote.noLinkHelp')}
        </p>
        <Link to="/candidats" className="mt-8 inline-block text-champagne-300 underline">
          {t('vote.seeCandidates')}
        </Link>
      </Container>
    )
  }

  if (categoriesQuery.isPending) {
    return (
      <Container className="py-24">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-8 h-64 w-full" />
      </Container>
    )
  }

  if (categoriesQuery.isError) {
    // Un lien refusé et un réseau coupé ne se traitent pas pareil : le
    // premier ne guérira pas, le second si. Les confondre envoyait des
    // convives chercher l'équipe pour une simple coupure de WiFi.
    const cause = categoriesQuery.error
    const rejected =
      cause instanceof ApiError && [401, 403, 404, 422].includes(cause.status)
    const guest = !linkToken || !linkAttendeeId
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-md text-content-primary">
          {rejected ? t('me.invalidLink') : t('vote.error.unreachable')}
        </h1>
        <p className="mt-4 max-w-lg text-content-secondary">
          {rejected
            ? guest
              ? t('vote.error.staleBallot')
              : t('me.invalidLinkHelp')
            : t('vote.error.unreachableHelp')}
        </p>
        {rejected && guest && eventQuery.data?.slug ? (
          // Un bulletin de passage que le serveur ne reconnaît plus ne vote
          // plus nulle part : en demander un neuf ne double aucune voix.
          <Button
            className="mt-8"
            onClick={() => {
              forgetBallot(eventQuery.data.slug)
              window.location.reload()
            }}
          >
            {t('vote.error.newBallot')}
          </Button>
        ) : null}
        {!rejected ? (
          <Button className="mt-8" onClick={() => void categoriesQuery.refetch()}>
            {t('vote.error.retry')}
          </Button>
        ) : null}
      </Container>
    )
  }

  // Un bulletin de juré ne vote qu'au départage, un bulletin de salle jamais :
  // montrer à chacun les scrutins de l'autre ne lui apprend que « pas pour
  // vous ». Le serveur dit lequel ; on ne garde que ce qui concerne ce votant.
  const categories = (categoriesQuery.data ?? []).filter(
    (category) => category.reason !== 'JURY_ONLY' && category.reason !== 'JURY_BALLOT_RESERVED',
  )

  return (
    <Container className="py-16">
      <Overline>{t('vote.overline')}</Overline>
      <h1 className="mt-3 font-display text-display-md text-content-primary">{t('vote.title')}</h1>

      {categories.length === 0 ? (
        <p className="mt-10 max-w-xl text-body-lg text-content-secondary">{t('vote.nothingOpen')}</p>
      ) : (
        <div className="mt-12 space-y-16">
          {categories.map((category) => (
            <CategoryBlock
              key={category.id}
              category={category}
              highlight={highlight}
              onVote={(candidateId) => {
                setFailure(null)
                cast.mutate(
                  { categoryId: category.id, candidateId },
                  {
                    // La voix est enregistrée : une gerbe d'or le dit mieux
                    // qu'un texte, dans une salle sombre et bruyante.
                    onSuccess: () => celebrate(),
                    onError: (cause) =>
                      setFailure({ categoryId: category.id, message: explain(cause) }),
                  },
                )
              }}
              onWithdraw={(candidateId) => {
                setFailure(null)
                withdraw.mutate(
                  { categoryId: category.id, candidateId },
                  {
                    onError: (cause) =>
                      setFailure({ categoryId: category.id, message: explain(cause) }),
                  },
                )
              }}
              busy={cast.isPending || withdraw.isPending}
              error={failure?.categoryId === category.id ? failure.message : null}
            />
          ))}
        </div>
      )}

      <Link to="/mon-billet" className="mt-16 inline-block text-champagne-300 underline">
        {t('vote.backToTickets')}
      </Link>
    </Container>
  )
}

function CategoryBlock({
  category,
  highlight,
  onVote,
  onWithdraw,
  busy,
  error,
}: {
  category: VotingCategory
  /** Le candidat dont on vient d'ouvrir le lien, s'il y en a un. */
  highlight: string | null
  onVote: (candidateId: string) => void
  onWithdraw: (candidateId: string) => void
  busy: boolean
  error: string | null
}) {
  const { t } = useTranslation()

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h2 className="font-display text-heading-lg text-content-primary">{category.name}</h2>
        <span className="font-mono text-sm tabular-nums text-champagne-300">
          {category.votes_used} / {category.max_votes_per_attendee}
        </span>
      </div>

      {category.description ? (
        <p className="mt-2 max-w-2xl text-content-secondary">{category.description}</p>
      ) : null}

      {/* Le temps qui reste, en gros chiffres. Un scrutin qu'on annonce « bientôt
          fermé » au micro se traîne ; un décompte visible décide les indécis —
          et c'est ce que l'organisateur a demandé pour que le vote ne
          s'éternise pas. La date de fermeture vient de la configuration : ici
          on ne fait que la montrer. */}
      {category.closes_at ? (
        <div className="mt-6">
          <Countdown target={category.closes_at} textBefore={t('vote.closesIn')} />
        </div>
      ) : null}

      {category.status === 'open' ? (
        <RoomCountdown endsAt={category.countdown_ends_at} serverNow={category.server_now} />
      ) : null}

      {/* Le motif vient du serveur : le bouton grisé n'est jamais muet. */}
      {!category.can_vote && category.reason ? (
        <p className="mt-4 rounded-lg border border-line px-4 py-3 text-sm text-content-muted">
          {t(`vote.reason.${category.reason}`)}
        </p>
      ) : null}

      {error ? <p className="mt-4 text-sm text-state-danger">{error}</p> : null}

      <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {category.nominees.map((nominee) => {
          const chosen = category.my_candidate_ids.includes(nominee.candidate_id)

          return (
            <li
              key={nominee.candidate_id}
              id={`candidat-${nominee.candidate_id}`}
              className="scroll-mt-24"
            >
              <Card
                className={cn(
                  'flex h-full flex-col gap-4 p-4',
                  chosen && 'hairline-champagne',
                  // Arrivé par le lien d'un candidat : sa fiche se distingue,
                  // sinon on la cherche au milieu des autres.
                  highlight === nominee.candidate_id && 'ring-2 ring-champagne-400/60',
                )}
              >
                <div className="flex items-center gap-4">
                  {nominee.photo_url ? (
                    <img
                      src={nominee.photo_url}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-bg-overlay font-display text-heading-sm text-content-muted">
                      {nominee.candidate_number ?? '?'}
                    </div>
                  )}

                  <div className="min-w-0">
                    <p className="font-display text-heading-sm text-content-primary">
                      {nominee.name}
                    </p>
                    {nominee.candidate_number !== null ? (
                      <p className="font-mono text-caption text-content-muted">
                        #{nominee.candidate_number}
                      </p>
                    ) : null}
                    {/* Le lien mène **au vote**, pas à la vitrine.
                        Quelqu'un qui le reçoit ouvre la page sur cette fiche,
                        obtient son bulletin sans rien installer, et vote. Le
                        renvoyer vers la page des candidats l'obligerait à
                        retrouver ensuite comment voter — et beaucoup
                        abandonneraient là. */}
                    <button
                      type="button"
                      onClick={() =>
                        void navigator.clipboard?.writeText(
                          `${window.location.origin}/vote?candidat=${nominee.candidate_id}`,
                        )
                      }
                      className="mt-1 text-caption text-champagne-300 underline underline-offset-4"
                    >
                      {t('vote.copyProfile')}
                    </button>
                  </div>
                </div>

                {nominee.bio ? (
                  <p className="line-clamp-3 text-sm text-content-secondary">{nominee.bio}</p>
                ) : null}

                <div className="mt-auto">
                  {chosen ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge tone="champagne">{t('vote.chosen')}</Badge>
                      {category.allow_change_vote ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onWithdraw(nominee.candidate_id)}
                          className="text-caption text-content-muted underline"
                        >
                          {t('vote.withdraw')}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    // Cible tactile pleine largeur : on vote avec un pouce.
                    <Button
                      className="w-full"
                      disabled={!category.can_vote || busy}
                      onClick={() => onVote(nominee.candidate_id)}
                    >
                      {t('vote.cast')}
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          )
        })}
      </ul>

      {category.live_results ? <LiveResults scores={category.live_results} /> : null}
    </section>
  )
}

/**
 * Le décompte annoncé au micro, en petit : un rappel, pas une alarme.
 *
 * À zéro, le vote n'est PAS fermé — le MC ferme quand la salle a fini. On dit
 * donc « le vote se termine bientôt », et les boutons restent actifs tant que
 * le serveur accepte les voix.
 */
function RoomCountdown({
  endsAt,
  serverNow,
}: {
  endsAt: string | null | undefined
  serverNow: string | null | undefined
}) {
  const { t } = useTranslation()
  const remaining = useCountdown(endsAt, serverNow)
  if (remaining === null) return null
  return (
    <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-ember-400/40 px-4 py-1.5 text-sm text-content-secondary">
      {remaining > 0 ? (
        <>
          {t('voteTimer.publicRemaining')}
          <span className="font-mono tabular-nums text-ember-300">{formatClock(remaining)}</span>
        </>
      ) : (
        t('voteTimer.publicEnding')
      )}
    </p>
  )
}

/** Affiché uniquement si le serveur a envoyé des scores. */
function LiveResults({ scores }: { scores: VoteScore[] }) {
  const { t } = useTranslation()
  const highest = Math.max(...scores.map((score) => score.votes), 1)

  return (
    <div className="mt-8">
      <p className="overline text-content-muted">{t('vote.results')}</p>
      <ul className="mt-4 space-y-3">
        {scores.map((score) => (
          <li key={score.candidate_id ?? score.name}>
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-content-primary">{score.name}</span>
              <span className="font-mono tabular-nums text-champagne-300">
                {score.votes} · {score.share}%
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-bg-overlay">
              <div
                className="h-full rounded-full bg-ember"
                style={{ width: `${(score.votes / highest) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
